/**
 * ค่าคอมมิชชั่น ร่ำรวย — HR กรอกยอดขาย/คลิป ต่อคน ต่อรอบ แล้วระบบคำนวณให้
 * (SA/CS/MKT + %วันทำงาน + หัก + carry-forward). ผลคำนวณมาจาก server
 * (lib/ruaylonfa-commission-calc.ts) = แหล่งความจริงเดียว ไม่คำนวณซ้ำฝั่ง client.
 *
 * Phase 3 จะเติมวันทำงาน/หักลำดับหยุด/หักมาสาย อัตโนมัติจาก attendance/dayoff.
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useCommissionInputs, useSaveCommissionInput } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { Percent, ChevronDown, Save } from 'lucide-react';

type Dept = 'SA' | 'CS' | 'MKT';
type Clip = { grade: string; count: number; rate: number };

interface Result {
  base: number;
  workdayPercent: number;
  csRatePercent: number | null;
  net: number;
  payout: number;
  carryOut: number;
}
interface Row {
  emp_code: string;
  name: string;
  department: string | null;
  saved: boolean;
  dept?: Dept;
  sales?: number;
  clips?: Clip[];
  days_worked?: number | null;
  days_in_cycle?: number;
  dayoff_deduction?: number;
  late_deduction?: number;
  carry_in: number;
  result: Result | null;
}

interface FormState {
  dept: Dept;
  sales: string;
  clips: Clip[];
  days_worked: string;
  days_in_cycle: string;
  dayoff_deduction: string;
  late_deduction: string;
}

const CLIP_TEMPLATE: Clip[] = [
  { grade: 'A', count: 0, rate: 1200 },
  { grade: 'B', count: 0, rate: 700 },
  { grade: 'C', count: 0, rate: 300 },
];

function getLast6Periods(): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}
function daysInMonth(period: string): number {
  const [y, m] = period.split('-').map(Number);
  return y && m ? new Date(y, m, 0).getDate() : 30;
}
function baht(n: number): string {
  return n.toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function mergeClips(saved: Clip[] | undefined): Clip[] {
  return CLIP_TEMPLATE.map((t) => {
    const s = (saved ?? []).find((c) => c.grade === t.grade);
    return { grade: t.grade, count: s?.count ?? 0, rate: s?.rate ?? t.rate };
  });
}

export function HRCommission() {
  const [period, setPeriod] = useState<string>(getLast6Periods()[0] ?? '');
  const { data, isLoading } = useCommissionInputs(period) as { data: { rows: Row[] } | undefined; isLoading: boolean };
  const save = useSaveCommissionInput();
  const toast = useToast();
  const [openEmp, setOpenEmp] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);

  const rows = data?.rows ?? [];

  function openEditor(r: Row) {
    if (openEmp === r.emp_code) {
      setOpenEmp(null);
      setForm(null);
      return;
    }
    setOpenEmp(r.emp_code);
    setForm({
      dept: r.dept ?? 'CS',
      sales: String(r.sales ?? ''),
      clips: mergeClips(r.clips),
      days_worked: r.days_worked != null ? String(r.days_worked) : '',
      days_in_cycle: String(r.days_in_cycle ?? daysInMonth(period)),
      dayoff_deduction: String(r.dayoff_deduction ?? ''),
      late_deduction: String(r.late_deduction ?? ''),
    });
  }

  async function handleSave(empCode: string) {
    if (!form) return;
    try {
      await save.mutateAsync({
        emp_code: empCode,
        period,
        dept: form.dept,
        sales: Number(form.sales) || 0,
        clips: form.clips,
        days_worked: form.days_worked === '' ? null : Number(form.days_worked),
        days_in_cycle: Number(form.days_in_cycle) || daysInMonth(period),
        dayoff_deduction: Number(form.dayoff_deduction) || 0,
        late_deduction: Number(form.late_deduction) || 0,
      });
      toast.success('บันทึกแล้ว');
      setOpenEmp(null);
      setForm(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="ค่าคอมมิชชั่น">
      <div className="card p-4 mb-4">
        <label className="label">เลือกงวด</label>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className="input">
          {getLast6Periods().map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-2">
          กรอกยอดขาย (SA/CS) หรือจำนวนคลิป (MKT) + วันทำงาน แล้วระบบคำนวณค่าคอมให้ · ค่าติดลบยกไปหักงวดถัดไป
        </p>
      </div>

      {isLoading ? (
        <p className="text-center text-gray-400 py-8 text-sm">กำลังโหลด…</p>
      ) : rows.length === 0 ? (
        <p className="text-center text-gray-400 py-8 text-sm">ไม่มีพนักงาน</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const isOpen = openEmp === r.emp_code;
            return (
              <div key={r.emp_code} className="card p-0 overflow-hidden">
                <button
                  type="button"
                  onClick={() => openEditor(r)}
                  className="w-full flex items-center justify-between p-3 text-left"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{r.name}</p>
                    <p className="text-xs text-gray-500">
                      {r.department ?? '—'}
                      {r.saved && r.dept ? ` · ${r.dept}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.result ? (
                      <span className="text-sm font-semibold text-[#2f7d52]">{baht(r.result.payout)} ฿</span>
                    ) : (
                      <span className="text-xs text-gray-400">ยังไม่กรอก</span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {isOpen && form && (
                  <div className="border-t border-gray-100 p-3 space-y-3 bg-gray-50/50">
                    {/* แผนก */}
                    <div>
                      <label className="label">แผนก (วิธีคิด)</label>
                      <div className="grid grid-cols-3 gap-2">
                        {(['SA', 'CS', 'MKT'] as Dept[]).map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setForm({ ...form, dept: d })}
                            className={`py-1.5 rounded-lg text-sm font-medium border ${
                              form.dept === d ? 'bg-brand-500 text-white border-brand-500' : 'bg-white border-gray-200'
                            }`}
                          >
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* SA/CS = ยอดขาย · MKT = คลิป */}
                    {form.dept === 'MKT' ? (
                      <div>
                        <label className="label">จำนวนคลิป × เรต</label>
                        <div className="space-y-2">
                          {form.clips.map((c, i) => (
                            <div key={c.grade} className="flex items-center gap-2">
                              <span className="w-8 text-sm font-semibold text-gray-600">{c.grade}</span>
                              <input
                                type="number"
                                inputMode="numeric"
                                value={c.count}
                                onChange={(e) => {
                                  const clips = [...form.clips];
                                  clips[i] = { ...c, count: Number(e.target.value) || 0 };
                                  setForm({ ...form, clips });
                                }}
                                placeholder="จำนวน"
                                className="input flex-1"
                              />
                              <span className="text-gray-400 text-sm">×</span>
                              <input
                                type="number"
                                inputMode="numeric"
                                value={c.rate}
                                onChange={(e) => {
                                  const clips = [...form.clips];
                                  clips[i] = { ...c, rate: Number(e.target.value) || 0 };
                                  setForm({ ...form, clips });
                                }}
                                placeholder="เรต"
                                className="input w-24"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="label">ยอดขายสุทธิ (บาท)</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={form.sales}
                          onChange={(e) => setForm({ ...form, sales: e.target.value })}
                          className="input"
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="label">วันมาทำงาน</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={form.days_worked}
                          onChange={(e) => setForm({ ...form, days_worked: e.target.value })}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="label">วันในรอบ</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={form.days_in_cycle}
                          onChange={(e) => setForm({ ...form, days_in_cycle: e.target.value })}
                          className="input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="label">หักลำดับหยุด</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={form.dayoff_deduction}
                          onChange={(e) => setForm({ ...form, dayoff_deduction: e.target.value })}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="label">หักมาสาย</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={form.late_deduction}
                          onChange={(e) => setForm({ ...form, late_deduction: e.target.value })}
                          className="input"
                        />
                      </div>
                    </div>

                    {r.carry_in > 0 && (
                      <p className="text-xs text-amber-700">ยกยอดหักจากงวดก่อน: {baht(r.carry_in)} ฿</p>
                    )}

                    {/* ผลคำนวณล่าสุด (จาก server หลังบันทึก) */}
                    {r.result && (
                      <div className="rounded-lg bg-white border border-gray-200 p-3 text-sm space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-500">%วันทำงาน</span>
                          <span>{r.result.workdayPercent}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">ฐานคอม</span>
                          <span>{baht(r.result.base)} ฿</span>
                        </div>
                        <div className="flex justify-between font-semibold">
                          <span>จ่ายจริงงวดนี้</span>
                          <span className="text-[#2f7d52]">{baht(r.result.payout)} ฿</span>
                        </div>
                        {r.result.carryOut > 0 && (
                          <div className="flex justify-between text-amber-700">
                            <span>ยกไปหักงวดหน้า</span>
                            <span>{baht(r.result.carryOut)} ฿</span>
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleSave(r.emp_code)}
                      disabled={save.isPending}
                      className="btn-primary w-full justify-center disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      {save.isPending ? 'กำลังบันทึก…' : 'บันทึก & คำนวณ'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-gray-400 mt-4 flex items-center gap-1">
        <Percent className="w-3 h-3" /> วันทำงาน/หักลำดับหยุด/หักมาสาย ระยะนี้กรอกเอง — เฟสถัดไปจะดึงอัตโนมัติจากระบบลงเวลา
      </p>
    </AdminLayout>
  );
}
